import { Button, Link, Text } from "@zapier/design-system";
import { useAtomValue, useSetAtom, useAtom } from "jotai";
import { useCallback, useEffect } from "react";
import { CREATE_NEW_INTERFACE_BUTTON_TEXT } from "@zapier/canvas-constants";
import {
  createNewInterfaceAsyncAtom,
  interfacesAsyncAtom,
  interfacesUsageAsyncAtom,
  shouldCreateNewInterfaceAtom,
  updateNodeAtom,
  AllPlansType,
  getUpgradePlanType,
  emitEventOnInterfacesPlanUpgradeAtom,
  saveCanvasAsyncCommand,
} from "@zapier/canvas-state";

import useIsOwnerAdminOrSuperAdmin from "../../hooks/useIsOwnerAdminOrSuperAdmin";
import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
export default function CreateNewInterfaceButton({
  nodeId,
}: {
  nodeId: string;
}) {
  const setShouldCreateNewInterface = useSetAtom(shouldCreateNewInterfaceAtom);
  const createNewInterfaceResponse = useAtomValue(createNewInterfaceAsyncAtom);
  const updateNode = useSetAtom(updateNodeAtom);
  const saveToBackend = useSetAtom(saveCanvasAsyncCommand);
  const { emitEventOnInterfaceCreation } = useEmitResourceCreatedEvents();
  const refreshInterfacesProjects = useSetAtom(interfacesAsyncAtom);

  const [interfacesUsage, refreshInterfacesUsage] = useAtom(
    interfacesUsageAsyncAtom,
  );

  const createNewInterface = useCallback(() => {
    setShouldCreateNewInterface(true);
  }, [setShouldCreateNewInterface]);

  // Reset the shouldCreateNewInterfaceAtom to false after the createNewInterfaceResponse completes
  useEffect(() => {
    if (createNewInterfaceResponse !== null) {
      const pagesKeys = Object.keys(createNewInterfaceResponse.pages || {});
      const pageId = pagesKeys.length > 0 ? pagesKeys[0] : undefined;
      const projectId = createNewInterfaceResponse.id;

      if (projectId) {
        const updatedNodes = updateNode({
          id: nodeId,
          data: {
            projectId,
            pageId,
          },
        });
        saveToBackend({ nodes: updatedNodes });
        refreshInterfacesProjects(); // Trigger interfaces projects to be re-fetched, so the new interface can be populated in the InterfacesProjects Typeahead
        refreshInterfacesUsage(); // Because projectCount would've changed
        setShouldCreateNewInterface(false);

        emitEventOnInterfaceCreation(projectId);
      }
    }
  }, [
    refreshInterfacesUsage,
    createNewInterfaceResponse,
    setShouldCreateNewInterface,
    saveToBackend,
    updateNode,
    emitEventOnInterfaceCreation,
    refreshInterfacesProjects,
    nodeId,
  ]);

  return (
    <>
      <Button
        color="primary"
        iconBefore="formAdd"
        onClick={createNewInterface}
        disabled={interfacesUsage?.needsToUpgrade ?? false}
      >
        {CREATE_NEW_INTERFACE_BUTTON_TEXT}
      </Button>
      {interfacesUsage?.needsToUpgrade && (
        <UpsellMessage
          currentPlanName={interfacesUsage.currentInterfacesPlanName}
        />
      )}
    </>
  );
}

function UpsellMessage({ currentPlanName }: { currentPlanName: AllPlansType }) {
  const isAdmin = useIsOwnerAdminOrSuperAdmin();
  const emitEventOnInterfacesPlanUpgrade = useSetAtom(
    emitEventOnInterfacesPlanUpgradeAtom,
  );
  const planType = getUpgradePlanType({ type: "interfaces", currentPlanName });
  const href = `/app/planbuilder/add-ons?product=interfaces-${planType}`;

  if (isAdmin) {
    return (
      <Text tag="p" type="SmallPrint1" textAlign="left" color="GrayWarm10">
        You&apos;ve reached your plans Interfaces limit.&nbsp;
        <Link
          href={href}
          target="_blank"
          onClick={() =>
            emitEventOnInterfacesPlanUpgrade(
              "Upgrade to create more Interfaces.",
            )
          }
        >
          Upgrade
        </Link>
        &nbsp;to create more Interfaces.
      </Text>
    );
  }

  return (
    <Text tag="p" type="SmallPrint1" textAlign="left" color="GrayWarm10">
      You&apos;ve reached your plans Interfaces limit. Talk to your admin about
      upgrading to create more Interfaces.
    </Text>
  );
}
